import { STRUCTURES_API } from '@/api';
import utils from '@/utils';
import { clone, handleError } from '@/utils/dataUtil';

const state = {
  tableKey: -999,
  currentTableRow: null,
  tableData: [],
  tableConfig: {
    columns: [{
      prop: 'participantName', label: 'Participant Name', sortable: true,
    }, {
      prop: 'commodityName', label: 'Commodity Name', sortable: true,
    }, {
      prop: 'marketName', label: 'Market', sortable: true,
    }, {
      prop: 'company', label: 'Company', sortable: true,
    }, {
      prop: 'defaultFlag', label: 'Default?', sortable: true,
    }, {
      prop: 'createdBy', label: 'Created By', sortable: true,
    }, {
      prop: 'createdDate', label: 'Created Date', sortable: true, dataType: 'datetime',
    }, {
      prop: 'updatedBy', label: 'Updated By', sortable: true,
    }, {
      prop: 'updatedDate', label: 'Updated Date', sortable: true, dataType: 'datetime',
    }],
  },
  nullTableRow: {
    id: -999,
    versionId: null,
    marketName: null,
    commodityName: null,
    companyId: null,
    participantName: null,
    defaultFlag: false,
    activeFlag: true,
    createdBy: null,
    createdDate: null,
    updatedBy: null,
    updatedDate: null,
  },
};

const getters = {
  getTableConfig: (state) => state.tableConfig,
  getTableData: (state) => state.tableData,
  getCurrentRow: (state) => state.currentTableRow,
  getCompanyList: (state, localGetters, rootState) => rootState.lookup.companyList,
  getMarketList: (state, localGetters, rootState) => rootState.lookup.marketList,
  getEnergyCommodityList: (state, localGetters, rootState) => rootState.lookup.energyCommodityList,
};

const actions = {
  initialize({ dispatch }) {
    dispatch('lookup/fetchMarketList', null, { root: true });
    dispatch('lookup/fetchCompanyList', null, { root: true });
    dispatch('lookup/fetchEnergyCommodityList', null, { root: true });
    dispatch('loadTableData');
  },
  resetTable({ dispatch, commit, state }) {
    commit('resetTable');
  },
  loadTableData({ commit }) {
    STRUCTURES_API.get('/energy-market-participants').then(({ data }) => {
      commit('loadTableData', data.marketParticipants);
      this.$notify(`${data.marketParticipants.length} Market Participant(s) Loaded`, 'info');
    }).catch((err) => {
      this.$notify('Failed to Load Market Participant', 'error');
    });
  },
  createTableRow({ commit }) {
    commit('createTableRow');
  },
  postMarketParticipant({ commit, state }, marketParticipant) {
    STRUCTURES_API.post('/energy-market-participants', marketParticipant).then(({ data }) => {
      if (data.defaultFlag) {
        const oldDefault = state.tableData.find((x) => x.defaultFlag && x.company === data.company && x.marketName === data.marketName);
        if (oldDefault) {
          oldDefault.defaultFlag = false;
          commit('updateMarketParticipant', oldDefault);
        }
      }
      state.tableData.push(data);
      this.$notify('Market Participant Added', 'success');
    })
      .catch(handleError('Failed to Add Market Participant'));
  },
  updateMarketParticipant({ dispatch, commit, state }, marketParticipant) {
    STRUCTURES_API.put(`/energy-market-participants/${marketParticipant.id}`, marketParticipant).then(({ data }) => {
      if (data.defaultFlag) {
        const oldDefault = state.tableData.find((x) => x.defaultFlag && x.company === data.company && x.marketName === data.marketName);
        if (oldDefault) {
          oldDefault.defaultFlag = false;
          commit('updateMarketParticipant', oldDefault);
        }
      }
      commit('updateMarketParticipant', data);
      this.$notify('Market Participant Updated', 'success');
    })
      .catch(handleError('Failed to Update Market Participant'));
  },
  deleteTableRow({ dispatch, commit, state }) {
    STRUCTURES_API.delete(`/energy-market-participants/${state.currentTableRow.id}`).then(({ data }) => {
      commit('deleteTableRow');
      dispatch('currentTableRowChange', clone(state.nullTableRow));
      this.$notify('Market Participant Removed', 'success');
    }).catch((err) => {
      this.$notify('Failed to Remove Market Participant', 'error');
    });
  },
  currentTableRowChange({ commit }, currentRow) {
    commit('currentTableRowChange', currentRow);
  },
};

const mutations = {
  updateMarketParticipant(state, marketParticipant) {
    const rowIndex = state.tableData.findIndex(({ id }) => id === marketParticipant.id);
    state.tableData.splice(rowIndex, 1, marketParticipant);
  },
  currentTableRowChange(state, currentRow) {
    state.currentTableRow = currentRow;
  },
  resetTable(state) {
    state.tableData = [];
    state.currentTableRow = clone(state.nullTableRow);
  },
  loadTableData(state, marketParticipants) {
    state.tableData = marketParticipants;
  },
  createTableRow(state) {
    const marketParticipant = clone(state.nullTableRow);
    marketParticipant.id = state.tableKey++;
    marketParticipant.versionId = 1;
    marketParticipant.createdBy = this.getters['auth/getProfile'].userName;
    marketParticipant.createdDate = utils.date.getNow().utc().format();
    state.currentTableRow = marketParticipant;
  },
  deleteTableRow(state) {
    state.tableData = state.tableData.filter((marketParticipant) => marketParticipant.id !== state.currentTableRow.id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};